/**
 * Today — "next lesson begins in …" countdown (Anthony, 2026-09-13).
 *
 * One pure function behind the Up next card on the Today view (2026-10-08;
 * until then a pill on each of the My lessons / Full studio tabs). The page
 * calls it with the rows of the scope being looked at.
 *
 * Rules, each deliberate:
 *  - Only TODAY has a countdown. On any other date "in 4h" would be a lie
 *    about a different day, so the caller passes `isToday` and gets null.
 *  - Only LESSONS count (kind === 'lesson'). Events and closures affect the
 *    day but are not the thing a teacher is counting down to.
 *  - "Next" means the first lesson that has not been REGISTERED as started,
 *    not the first whose start time is still ahead (Anthony, 2026-11-01). A
 *    lesson whose time has come and gone with nobody starting it is still the
 *    thing the teacher has to do next, so the card stays on it, reading "Not
 *    started", instead of sliding to the following student by the clock.
 *    Registered means the session record says so (`rowIsSettled`): the timer
 *    stamped `startedAt` (Start, or the first jot), the lesson was completed,
 *    or an outcome was marked (a no-show or cancellation is settled too, and
 *    must not pin the card). A lesson in progress is still not "next".
 *  - Cancelled occurrences never reach the rows (the shared expander drops
 *    them), so nothing here has to reason about cancellation.
 *  - When nothing is left the result is still an object, not null, so the
 *    pill can say "Done" rather than vanish — an absent control reads as
 *    broken, and a keratoconus reader should never have to hunt for whether
 *    the pill exists.
 */

const parseHm = (hm) => {
	if (!hm || typeof hm !== 'string') return null;
	const [h, m] = hm.split(':').map(Number);
	if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
	return h * 60 + m;
};

/**
 * Has this lesson row been registered as started, or otherwise settled?
 * Reads the fields the page copies off the joined session record. A row with
 * no session record has not started, whatever the clock says.
 */
export const rowIsSettled = (r) => !!(r && (
	r.startedAt || r.completedAt
	|| r.status === 'in-progress' || r.status === 'completed'
	|| r.outcome
));

/** Minutes since local midnight for a Date. */
const minutesOfDay = (d) => d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;

/**
 * Human label for a positive number of minutes-until.
 * Whole minutes only: a seconds display flickers and helps nobody.
 * Under a minute reads "<1m" rather than "0m", which would look like "now".
 */
export const formatCountdown = (minutesUntil) => {
	if (minutesUntil < 1) return '<1m';
	const whole = Math.floor(minutesUntil);
	const h = Math.floor(whole / 60);
	const m = whole % 60;
	if (h === 0) return `${m}m`;
	return `${h}h ${String(m).padStart(2, '0')}m`;
};

/**
 * @param {Array<{kind:string,time:string,studentName?:string}>} rows
 * @param {Date} now
 * @param {boolean} isToday
 * @returns {null | { state:'next'|'none', minutes?:number, overdue?:boolean, label:string, tone:string, time?:string, studentName?:string }}
 */
export const nextLessonCountdown = (rows, now, isToday) => {
	if (!isToday) return null;
	const nowMin = minutesOfDay(now);
	let best = null;
	(rows || []).forEach((r) => {
		if (!r || r.kind !== 'lesson') return;
		const start = parseHm(r.time);
		if (start == null) return;
		if (rowIsSettled(r)) return; // started, finished or marked
		if (!best || start < best.start) best = { start, row: r };
	});
	if (!best) {
		return { state: 'none', label: 'Done', tone: 'neutral' };
	}
	const minutes = best.start - nowMin;
	// Its time has arrived and nobody has started it. No "N min late" figure on
	// purpose: that is its own card, not this rule.
	const overdue = minutes <= 0;
	return {
		state: 'next',
		minutes,
		overdue,
		label: overdue ? 'Not started' : `Next in ${formatCountdown(minutes)}`,
		// Mirrors the row badge: warning inside the 15-minute "Starting Soon" window.
		tone: minutes <= 15 ? 'warning' : 'info',
		time: best.row.time,
		studentName: best.row.studentName || null,
		// The row itself, so the Up next card (2026-10-08) renders the same
		// lesson the countdown names: one source, never two that can disagree.
		row: best.row,
	};
};
